import { useCallback, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateProfile, useUploadImage } from "@/api/hooks";
import { useProfileStore } from "../store/profile-store";
import { useSettingsFormState, useSettingsLoading } from "../../store";
import useAuthStore from "@/store/use-auth-store";
import { useToast } from "@/features/toast";
import { getFileUrl } from "@/lib/utils";
import type { UpdateProfileRequest } from "@/api/models/api-types";
import { normalizeGender } from "../utils/gender";

/**
 * Profile Feature Hook
 * Combines API calls, store state, and business logic
 * Following zero props pattern - components use this hook directly
 */
export const useProfile = () => {
  const { t } = useTranslation();
  const { success, error } = useToast();

  // Auth Store
  const { user } = useAuthStore();

  // API Hooks
  const updateProfileMutation = useUpdateProfile();
  const uploadImageMutation = useUploadImage();

  // Profile Store
  const {
    isAvatarUploadModalOpen,
    isDeleteAccountDialogOpen,
    isDirty,
    uploadingAvatar,
    avatarPreview,
    selectedFile,
    formData,
    openAvatarUploadModal,
    closeAvatarUploadModal,
    openDeleteAccountDialog,
    closeDeleteAccountDialog,
    setIsDirty,
    setUploadingAvatar,
    setAvatarPreview,
    setSelectedFile,
    updateFormData,
    setFormData,
    resetFormData,
  } = useProfileStore();

  // Settings Store
  const { hasUnsavedChanges, setHasUnsavedChanges, markAsSaved } =
    useSettingsFormState("profile");
  const { isTabLoading, setTabLoading } = useSettingsLoading("profile");

  // Seed form data from user once available / after refresh
  useEffect(() => {
    if (!user?._id) return;

    setFormData({
      name: user.name || "",
      region: user.region || "",
      phoneNumber: user.phoneNumber || "",
      age: user.age?.toString() || "",
      height: user.height?.toString() || "",
      size: user.size || "",
      shoeSize: user.shoeSize?.toString() || "",
      gender: normalizeGender(user.gender),
    });
  }, [
    user?._id,
    user?.name,
    user?.region,
    user?.phoneNumber,
    user?.age,
    user?.height,
    user?.size,
    user?.shoeSize,
    user?.gender,
    setFormData,
  ]);

  // Computed Values
  const userImage = useMemo(() => {
    if (avatarPreview) return avatarPreview;
    if (!user?.image) return null;

    return getFileUrl(user.image);
  }, [user?.image, avatarPreview]);

  const userInitials = useMemo(() => {
    if (!user?.name) return "U";
    return user.name.charAt(0).toUpperCase();
  }, [user?.name]);

  const isUserDataLoaded = useMemo(() => {
    return !!user && !!user._id;
  }, [user]);

  const [isFormReady, setIsFormReady] = useState(false);

  useEffect(() => {
    if (isUserDataLoaded) {
      const timer = setTimeout(() => {
        setIsFormReady(true);
      }, 300);

      return () => clearTimeout(timer);
    }

    setIsFormReady(false);
  }, [isUserDataLoaded]);

  const baselineFormData = useMemo(
    () => ({
      name: user?.name || "",
      region: user?.region || "",
      phoneNumber: user?.phoneNumber || "",
      age: user?.age?.toString() || "",
      height: user?.height?.toString() || "",
      size: user?.size || "",
      shoeSize: user?.shoeSize?.toString() || "",
      gender: normalizeGender(user?.gender),
    }),
    [user]
  );

  const hasFormChanges = useMemo(() => {
    if (!user) return false;

    return (
      formData.name !== baselineFormData.name ||
      formData.region !== baselineFormData.region ||
      formData.phoneNumber !== baselineFormData.phoneNumber ||
      formData.age !== baselineFormData.age ||
      formData.height !== baselineFormData.height ||
      formData.size !== baselineFormData.size ||
      formData.shoeSize !== baselineFormData.shoeSize ||
      formData.gender !== baselineFormData.gender ||
      !!selectedFile
    );
  }, [user, formData, baselineFormData, selectedFile]);

  useEffect(() => {
    if (!isFormReady) return;
    setHasUnsavedChanges(hasFormChanges);
    setIsDirty(hasFormChanges);
  }, [hasFormChanges, setHasUnsavedChanges, setIsDirty, isFormReady]);

  const resetDraft = useCallback(() => {
    setFormData(baselineFormData);
    setAvatarPreview(null);
    setSelectedFile(null);
    setIsDirty(false);
    setHasUnsavedChanges(false);
  }, [
    baselineFormData,
    setFormData,
    setAvatarPreview,
    setSelectedFile,
    setIsDirty,
    setHasUnsavedChanges,
  ]);

  // Profile Update Handler
  const handleUpdateProfile = useCallback(
    async (profileData?: UpdateProfileRequest) => {
      if (!setTabLoading) return;

      try {
        setTabLoading(true);

        if (selectedFile) {
          setUploadingAvatar(true);
          await uploadImageMutation.mutateAsync(selectedFile);
          setUploadingAvatar(false);
          setSelectedFile(null);
        }

        const dataToSend = profileData || {
          name: formData.name,
          region: formData.region || undefined,
          phoneNumber: formData.phoneNumber || undefined,
          age: formData.age ? parseInt(formData.age, 10) : undefined,
          height: formData.height ? parseInt(formData.height, 10) : undefined,
          size: formData.size || undefined,
          shoeSize: formData.shoeSize
            ? parseInt(formData.shoeSize, 10)
            : undefined,
          gender: formData.gender || undefined,
        };

        await updateProfileMutation.mutateAsync(dataToSend);

        success(t("settings.profile.updateSuccess"));
        markAsSaved();
        setIsDirty(false);
      } catch (updateError: any) {
        console.error("Profile update error:", updateError);
        error(
          updateError?.response?.data?.message ||
            t("settings.profile.updateError")
        );
      } finally {
        setTabLoading(false);
      }
    },
    [
      updateProfileMutation,
      uploadImageMutation,
      setTabLoading,
      success,
      error,
      markAsSaved,
      setIsDirty,
      setUploadingAvatar,
      setSelectedFile,
      t,
      formData,
      selectedFile,
    ]
  );

  // Avatar Upload Handler
  const handleAvatarUpload = useCallback(
    async (file: File) => {
      try {
        setUploadingAvatar(true);

        const reader = new FileReader();
        reader.onloadend = () => {
          setAvatarPreview(reader.result as string);
        };
        reader.readAsDataURL(file);

        await uploadImageMutation.mutateAsync(file);

        success(t("settings.profile.avatarUploadSuccess"));
        closeAvatarUploadModal();
      } catch (uploadError: any) {
        console.error("Avatar upload error:", uploadError);
        error(t("settings.profile.avatarUploadError"));
      } finally {
        setUploadingAvatar(false);
      }
    },
    [
      setUploadingAvatar,
      setAvatarPreview,
      uploadImageMutation,
      success,
      error,
      closeAvatarUploadModal,
      t,
    ]
  );

  // Avatar Remove Handler
  const handleRemoveAvatar = useCallback(() => {
    setAvatarPreview(null);
    success(t("settings.profile.avatarRemoveSuccess"));
  }, [setAvatarPreview, success, t]);

  // Delete Account Handler
  const handleDeleteAccount = useCallback(async () => {
    try {
      success(t("settings.profile.deleteAccountSuccess"));
      closeDeleteAccountDialog();
    } catch (deleteError: any) {
      console.error("Delete account error:", deleteError);
      error(t("settings.profile.deleteAccountError"));
    }
  }, [success, error, closeDeleteAccountDialog, t]);

  return {
    // User Data
    user,
    userImage,
    userInitials,

    // API States
    isUpdating: updateProfileMutation.isPending,
    isUploadingAvatar: uploadingAvatar,

    // UI States
    isAvatarUploadModalOpen,
    isDeleteAccountDialogOpen,
    isDirty,
    hasUnsavedChanges,
    isTabLoading,
    isUserDataLoaded,
    isFormReady,

    // Actions
    updateProfile: handleUpdateProfile,
    uploadAvatar: handleAvatarUpload,
    removeAvatar: handleRemoveAvatar,
    deleteAccount: handleDeleteAccount,
    resetDraft,

    // Modal Actions
    openAvatarUploadModal,
    closeAvatarUploadModal,
    openDeleteAccountDialog,
    closeDeleteAccountDialog,

    // Form State Actions
    setIsDirty,
    setHasUnsavedChanges,
    setAvatarPreview,
    setSelectedFile,

    // Form Data
    formData,
    updateFormData,
    setFormData,
    resetFormData,
  };
};
